/**
 * Offline Complaint Dialog
 * Step-based dialog for creating offline complaints (Authority only)
 */

import React, { useState } from 'react';
import { BaseDialog } from './index';
import complaintService from '../../services/complaintService';
import Toast from '../Toast';
import { obfuscateEmail } from '../../utils/emailObfuscation';

const OfflineComplaintDialog = ({ isOpen, onClose, onSave }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    address: '',
    complaint: '',
    supportingDocuments: [],
    generalDocuments: [],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const steps = [
    { id: 1, title: 'Complainant Information', description: 'Personal details' },
    { id: 2, title: 'Complaint Details', description: 'Complaint information' },
    { id: 3, title: 'Review & Submit', description: 'Final review' }
  ];

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
      if (formData.email && !formData.email.includes('@')) {
        newErrors.email = 'Please enter a valid email';
      }
    }
    
    if (step === 2) {
      if (!formData.complaint.trim()) newErrors.complaint = 'Complaint details are required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = (type, files) => {
    const fileArray = Array.from(files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    const validFiles = [];
    const fileErrors = [];
    
    fileArray.forEach((file) => {
      if (file.size > maxSize) {
        fileErrors.push(`${file.name}: File size exceeds 5MB limit`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (fileErrors.length > 0) {
      setErrors(prev => ({
        ...prev,
        fileUpload: fileErrors.join(', ')
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.fileUpload;
        return newErrors;
      });
    }
    
    if (validFiles.length > 0) {
      setFormData(prev => ({ ...prev, [type]: [...prev[type], ...validFiles] }));
    }
  };

  const handleRemoveFile = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    // Prevent double submission
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const result = await complaintService.createOfflineComplaint(formData);
      
      if (result.success) {
        setToast({
          show: true,
          message: result.message || 'Offline complaint created successfully',
          type: 'success'
        });
        
        // Call parent callback
        onSave({ ...formData, complaintNumber: result.data?.complaint_number || result.data?.complaintNumber });
        
        // Reset form
        setFormData({
          name: '',
          mobile: '',
          email: '',
          address: '',
          complaint: '',
          supportingDocuments: [],
          generalDocuments: [],
        });
        setCurrentStep(1);
        setErrors({});
        
        // Close dialog after short delay
        setTimeout(() => {
          onClose();
          setToast({ show: false, message: '', type: 'success' });
        }, 1500);
      } else {
        setToast({
          show: true,
          message: result.message || 'Failed to create offline complaint',
          type: 'error'
        });
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error creating offline complaint:', error);
      setToast({
        show: true,
        message: 'Failed to create offline complaint. Please try again.',
        type: 'error'
      });
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      mobile: '',
      email: '',
      address: '',
      complaint: '',
      supportingDocuments: [],
      generalDocuments: [],
    });
    setCurrentStep(1);
    setErrors({});
    onClose();
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
            currentStep >= step.id 
              ? 'bg-primary-600 border-primary-600 text-white' 
              : 'border-gray-300 text-gray-500'
          }`}>
            <span className="text-sm font-semibold">{step.id}</span>
          </div>
          <div className="ml-3">
            <p className={`text-sm font-medium ${
              currentStep >= step.id ? 'text-primary-600' : 'text-gray-500'
            }`}>
              {step.title}
            </p>
            <p className="text-xs text-gray-400">{step.description}</p>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-12 h-0.5 mx-3 ${
              currentStep > step.id ? 'bg-primary-600' : 'bg-gray-300'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter complainant name"
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mobile Number *
        </label>
        <input
          type="tel"
          value={formData.mobile}
          onChange={(e) => handleInputChange('mobile', e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
            errors.mobile ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter mobile number"
        />
        {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter email address"
        />
        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address *
        </label>
        <textarea
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          placeholder="Enter address"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Complaint Details *
        </label>
        <textarea
          value={formData.complaint}
          onChange={(e) => handleInputChange('complaint', e.target.value)}
          rows={6}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
            errors.complaint ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter complaint details..."
        />
        {errors.complaint && <p className="mt-1 text-sm text-red-600">{errors.complaint}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Supporting Documents
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => handleFileUpload('supportingDocuments', e.target.files)}
            className="hidden"
            id="supportingDocs"
          />
          <label htmlFor="supportingDocs" className="cursor-pointer">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600">Click to upload supporting documents</p>
            <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG (Max 5MB each)</p>
          </label>
        </div>

        {formData.supportingDocuments.length > 0 && (
          <div className="mt-4 space-y-2">
            {formData.supportingDocuments.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center flex-1 min-w-0">
                  <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-900 truncate">{file.name}</span>
                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                    ({(file.size / 1024).toFixed(2)} KB)
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveFile('supportingDocuments', index)}
                  className="ml-3 text-red-600 hover:text-red-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        {errors.fileUpload && (
          <p className="mt-2 text-sm text-red-600">{errors.fileUpload}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          General Documents
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => handleFileUpload('generalDocuments', e.target.files)}
            className="hidden"
            id="generalDocs"
          />
          <label htmlFor="generalDocs" className="cursor-pointer">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600">Click to upload general documents</p>
            <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG (Max 5MB each)</p>
          </label>
        </div>

        {formData.generalDocuments.length > 0 && (
          <div className="mt-4 space-y-2">
            {formData.generalDocuments.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center flex-1 min-w-0">
                  <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-900 truncate">{file.name}</span>
                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                    ({(file.size / 1024).toFixed(2)} KB)
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveFile('generalDocuments', index)}
                  className="ml-3 text-red-600 hover:text-red-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        {errors.fileUpload && (
          <p className="mt-2 text-sm text-red-600">{errors.fileUpload}</p>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Complainant Information</h3>
        <div className="space-y-2">
          <p className="text-sm"><span className="font-medium">Name:</span> {formData.name}</p>
          <p className="text-sm"><span className="font-medium">Mobile:</span> {formData.mobile}</p>
          {formData.email && <p className="text-sm"><span className="font-medium">Email:</span> {obfuscateEmail(formData.email)}</p>}
          {formData.address && <p className="text-sm"><span className="font-medium">Address:</span> {formData.address}</p>}
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Complaint Details</h3>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{formData.complaint}</p>
      </div>

      {formData.supportingDocuments.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Supporting Documents ({formData.supportingDocuments.length})</h3>
          <div className="space-y-2">
            {formData.supportingDocuments.map((file, index) => (
              <p key={index} className="text-sm text-gray-700">{file.name} ({(file.size / 1024).toFixed(2)} KB)</p>
            ))}
          </div>
        </div>
      )}

      {formData.generalDocuments.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">General Documents ({formData.generalDocuments.length})</h3>
          <div className="space-y-2">
            {formData.generalDocuments.map((file, index) => (
              <p key={index} className="text-sm text-gray-700">{file.name} ({(file.size / 1024).toFixed(2)} KB)</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderFooterButtons = () => {
    if (currentStep === 1) {
      return (
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Next
          </button>
        </div>
      );
    } else if (currentStep === 2) {
      return (
        <div className="flex justify-end space-x-3">
          <button
            onClick={handlePrevious}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Next
          </button>
        </div>
      );
    } else {
      return (
        <div className="flex justify-end space-x-3">
          <button
            onClick={handlePrevious}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Complaint'}
          </button>
        </div>
      );
    }
  };

  return (
    <>
      <BaseDialog
        isOpen={isOpen}
        onClose={handleCancel}
        title="Create Offline Complaint"
        size="large"
        footer={renderFooterButtons()}
      >
        {renderStepIndicator()}
        
        <div className="mt-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>
      </BaseDialog>
      
      {toast.show && (
        <Toast
          isVisible={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </>
  );
};

export default OfflineComplaintDialog;

